import React, { useEffect, useMemo, useState } from "react"
import { Alert, Button, Form, Modal } from "react-bootstrap"
import { IBooking, getApiCallState, useStateSelector } from "./Model"
import { cancelBooking, setCancelModalVisibility } from "./Actions";
import { useDispatch } from "react-redux";
import { SpinnerSmall } from "../../Shared/Components/Spinner";
import { getStrings } from "../../Shared/Reservation/StringResources";
import { formatteraTal } from "../Shared/Utils";
import { clearApiCallState } from "../ApiCalls/Actions";
import { Direction } from "../Models";

const cancelStrings = getStrings().component.cancelBooking;
const bookingStrings = getStrings().component.booking;
const commonStrings = getStrings().component.common;

export const CancelBookingModal = () => {
	const dispatch = useDispatch();
	const state = useStateSelector(state => state.manageBooking);
	const apicalls = getApiCallState();
	const submitState = apicalls.getLoadingState("cancelBooking");

	const [direction, setDirection] = useState<Direction|"">("");

	const refundAmount = useMemo(() => getRefundAmount(state.booking, direction), [state.booking, direction]);

	const availableVoucherRefundAmount = state.booking?.paidWithVoucherAmount - state.booking?.refundedVoucherAmount || 0;
	const refundWithVoucherAmount = state.booking?.isRefundable ? Math.min(availableVoucherRefundAmount, refundAmount) : refundAmount;

	const canCancelOutbound = state.booking.outbound && state.booking.outbound.canBeCancelled && !state.booking.outbound.cancelled;
	const canCancelInbound = state.booking.inbound && state.booking.inbound.canBeCancelled && !state.booking.inbound.cancelled;

	const changeDirection = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!event.target.checked)
			return;
		setDirection(event.target.value as Direction);
	}

	const submitCancel = () => {
		dispatch(cancelBooking(state.bookingNumber, direction));
	};

	useEffect(() => {
		return () => {
			dispatch(clearApiCallState("cancelBooking"));
		};
	}, []);

	useEffect(() => {
		if (!state.booking)
			return;

		if (canCancelInbound && !canCancelOutbound)
			setDirection("inbound");
		else if (canCancelOutbound)
			setDirection("outbound");
	}, [state.booking]);

	if (!state.booking)
		return null;


	return <Modal show={state.showCancelModal} centered backdrop="static">
		<Modal.Header><Modal.Title>{cancelStrings.heading}</Modal.Title></Modal.Header>
		<Modal.Body>
			<p>{cancelStrings.infoText}</p>

			<dl>
				<dt>{bookingStrings.bookingNumber}</dt>
				<dd>{state.booking?.bookingNumber}</dd>
				<dt>{bookingStrings.totalPrice}</dt>
				<dd>{formatteraTal(state.booking?.totalPrice, commonStrings.sek)}</dd>
				<dt>{cancelStrings.refundAmount}</dt>
				<dd>{formatteraTal(getRefundAmount(state.booking, direction), commonStrings.sek)}</dd>
				{ refundWithVoucherAmount > 0 && <>
					<dt>{cancelStrings.voucherAmount}</dt>
					<dd>{formatteraTal(refundWithVoucherAmount, commonStrings.sek)}</dd>
				</>}
			</dl>

			<p>{ cancelStrings.refundInfo }</p>

			{ state.booking.outbound && state.booking.inbound && <>
				<h5>{cancelStrings.directionLabel}</h5>
				<Form.Check id="both" checked={direction === ""} type="radio" label={cancelStrings.directionBoth} name="direction" value="" onChange={changeDirection} disabled={!canCancelInbound || !canCancelOutbound} />
				<Form.Check id="outbound" checked={direction === "outbound"} type="radio" label={cancelStrings.directionOutbound} name="direction" value="outbound" disabled={!canCancelOutbound} onChange={changeDirection}/>
				<Form.Check id="inbound" checked={direction === "inbound"} type="radio" label={cancelStrings.directionInbound} name="direction" value="inbound" disabled={!canCancelInbound} onChange={changeDirection}/>
			</>}

			<Alert variant="danger" show={submitState === "error"}>{cancelStrings.cancelFailed}</Alert>

		</Modal.Body>
		<Modal.Footer>
			<Button variant="secondary" disabled={submitState === "loading"} onClick={() => dispatch(setCancelModalVisibility(false))}>{cancelStrings.cancelButtonText}</Button>
			<Button variant="danger" disabled={submitState === "loading"} onClick={submitCancel}>{cancelStrings.submitButtonText} {submitState === "loading" && <SpinnerSmall />}</Button>
		</Modal.Footer>

	</Modal>
}

const getRefundAmount = (booking: IBooking, direction: Direction|"") => {
	if (direction === "")
        return getRefundAmount(booking, "outbound") + getRefundAmount(booking, "inbound");

    if (direction === "outbound" && booking.outbound)
        return booking.outbound.cancelled ? 0 : booking.outbound.refundAmount;

    if (direction === "inbound" && booking.inbound)
        return booking.inbound.cancelled ? 0 : booking.inbound.refundAmount;

    return 0;
}